import { Button } from '@ui';
import { InlineSwitch } from '@ui/InlineSwitch';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useModal } from '../../../../hooks/useModal';
import { useClubSlug } from '../../../Clubs/hooks';
import Loader from '../../../Loader';
import { ClubMember } from '../../types';

const ClubMemberPreview = dynamic(() => import('../ClubMemberPreview'));

interface Props {
    clubMember: ClubMember;
}

const ContributionsPreview = ({ clubMember }: Props) => {
    const { t } = useTranslation('member-management');
    const clubSlug = useClubSlug();
    const [setModal] = useModal();
    const { data: contributionsData, isLoading: isLoadingContributions } = useSWR(
        clubMember ? `club-member/${clubMember?.id}/contributions` : null
    );
    // Show admission fee only when the member joined in the current year
    const entryYear = clubMember?.entry_date ? new Date(clubMember.entry_date).getFullYear() : null;
    const [showAdmissionFee, setShowAdmissionFee] = useState(entryYear === new Date().getFullYear());

    const showClubMemberPreviewForGroupHead = (clubMemberId: any) =>
        setModal({
            title: null,
            content: <ClubMemberPreview clubMemberId={clubMemberId} clubSlug={clubSlug} />,
            show: true,
            size: 80
        });

    const overflow =
        Number(contributionsData?.total?.contributions) +
        Number(contributionsData?.total?.locker_fee ?? 0) +
        Number(contributionsData?.total?.admission_fee) -
        Number(contributionsData?.group_invoice_limit);
    const normalizedOverflow = overflow < 0 ? 0 : overflow;

    return (
        <Loader isLoading={isLoadingContributions}>
            <div className="tw-mb-2 tw-flex tw-items-center tw-justify-end tw-gap-4">
                <span className="tw-text-accents-6">{t('clubMember.preview.contributions.showAdmissionFee')}</span>
                <InlineSwitch checked={showAdmissionFee} onChange={setShowAdmissionFee} />
            </div>
            {contributionsData?.group_head && (
                <Button
                    size="xs"
                    color="secondary"
                    className="tw-my-4 !tw-w-full"
                    onPress={() => showClubMemberPreviewForGroupHead(contributionsData?.group_head?.id)}
                >
                    {t('clubMember.preview.contributions.showGroupHead', {
                        name: contributionsData?.group_head?.name
                    })}
                </Button>
            )}
            <div className="tw-grid tw-grid-cols-3 tw-gap-y-2">
                {contributionsData?.categories
                    ?.filter((c) => (!showAdmissionFee ? !('admission_fee' in c) || 'contributions' in c : true))
                    .map((category) => (
                        <>
                            <div className="tw-col-span-2 tw-flex tw-flex-col tw-font-bold tw-leading-4 tw-text-accents-7">
                                <span className="tw-truncate">{category.title}</span>
                                {category.for_member_name && (
                                    <span className="tw-text-xs tw-leading-3 tw-text-accents-5">
                                        {category.for_member_name}
                                    </span>
                                )}
                            </div>
                            <span className="tw-text-right">
                                {Number(
                                    category.contributions ?? category.locker_fee ?? category.admission_fee
                                ).toFixed(2)}{' '}
                                €
                                {'contributions' in category
                                    ? ' | ' + (category.contributions_tax_percentage ?? '-') + ' %'
                                    : 'locker_fee' in category
                                      ? ' | ' + (category.locker_fee_tax_percentage ?? '-') + ' %'
                                      : null}
                            </span>
                        </>
                    ))}
                {contributionsData?.group_member_categories
                    ?.filter((c) => (!showAdmissionFee ? !('admission_fee' in c) || 'contributions' in c : true))
                    .map((category) => (
                        <>
                            <div className="tw-col-span-2 tw-flex tw-flex-col tw-font-bold tw-leading-4 tw-text-accents-7">
                                <span className="tw-truncate">{category.title}</span>
                                {category.for_member_name && (
                                    <span className="tw-text-xs tw-leading-3 tw-text-accents-5">
                                        {category.for_member_name}
                                    </span>
                                )}
                            </div>
                            <span className="tw-text-right">
                                {Number(
                                    category.contributions ?? category.locker_fee ?? category.admission_fee
                                ).toFixed(2)}{' '}
                                €
                                {'contributions' in category
                                    ? ' | ' + (category.contributions_tax_percentage ?? '-') + ' %'
                                    : 'locker_fee' in category
                                      ? ' | ' + (category.locker_fee_tax_percentage ?? '-') + ' %'
                                      : null}
                            </span>
                        </>
                    ))}
                <span className="tw-col-span-3 tw-my-2 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2" />
                <span className="tw-col-span-2 tw-font-bold tw-text-accents-7">
                    {t('clubMember.preview.contributions.total')}
                </span>
                <span className="tw-text-right tw-font-bold">
                    {(
                        Number(contributionsData?.total?.contributions_complete ?? 0) +
                        Number(contributionsData?.total?.locker_fee_complete ?? 0) +
                        (showAdmissionFee ? Number(contributionsData?.total?.admission_fee_complete ?? 0) : 0)
                    ).toFixed(2)}{' '}
                    €
                </span>
                <span className="tw-col-span-2 tw-font-bold tw-text-accents-7">
                    {t('clubMember.preview.contributions.totalForInvoiceLimit')}
                </span>
                <span className="tw-text-right tw-font-bold">
                    {(
                        Number(contributionsData?.total?.contributions ?? 0) +
                        Number(contributionsData?.total?.locker_fee ?? 0) +
                        (showAdmissionFee ? Number(contributionsData?.total?.admission_fee ?? 0) : 0)
                    ).toFixed(2)}{' '}
                    €
                </span>
                <span className="tw-bold tw-col-span-3 tw-my-2 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2" />
                {contributionsData?.group_invoice_limit && (
                    <>
                        <span className="tw-col-span-2 tw-font-bold tw-text-accents-7">
                            {t('clubMember.preview.contributions.invoiceLimit')}
                        </span>
                        <span className="tw-text-right tw-font-bold">
                            {Number(contributionsData?.group_invoice_limit ?? 0).toFixed(2)} €
                        </span>
                        <span className="tw-col-span-2 tw-font-bold tw-text-accents-7">
                            {t('clubMember.preview.contributions.amountOverInvoiceLimit')}
                        </span>
                        <span className="tw-text-right tw-font-bold">{normalizedOverflow.toFixed(2)} €</span>
                    </>
                )}
                {showAdmissionFee && (
                    <>
                        <span className="tw-col-span-2 tw-font-bold tw-text-accents-7">
                            {t('clubMember.preview.contributions.totalAdmissionFee')}
                        </span>
                        <span className="tw-text-right tw-font-bold">
                            {Number(contributionsData?.total?.admission_fee_complete ?? 0).toFixed(2)} €
                        </span>
                        <span className="tw-col-span-2 tw-font-bold tw-text-accents-7">
                            {t('clubMember.preview.contributions.totalAdmissionFeeForInvoiceLimit')}
                        </span>
                        <span className="tw-text-right tw-font-bold">
                            {Number(contributionsData?.total?.admission_fee ?? 0).toFixed(2)} €
                        </span>
                    </>
                )}
            </div>
        </Loader>
    );
};

export default ContributionsPreview;
